import React, { useCallback, useRef, useState } from 'react';
import BasicSelect from './components/BasicSelect/BasicSelect';
import GraphicalSelect from './components/GraphicalSelect/GraphicalSelect';
import OptionsList from './components/OptionsList/OptionsList';
import { GraphicalSelectProps } from './components/GraphicalSelect/GraphicalSelect.types';
import BasicSelectProps from './components/BasicSelect/BasicSelect.types';
import { IndexableSelectOption, SelectOption } from './Select.types';

// Type here to avoid cyclic imports.
export type SelectProps = BasicSelectProps | GraphicalSelectProps;

const Select = (props: SelectProps) => {
  const selectId = useRef(`id_${Math.random() * 1000}`);

  const {
    selectedValue,
    graphical,
    initialiseAsNull,
    nullText,
    controlled,
    placeholder,
    options,
    controlledText,
    className,
    defaultValue,
    color = 'primary',
    invalid,
    errorMsgHolderId,
    value,
    onSelectValue,
  } = props;

  if (!graphical) {
    return <BasicSelect {...props} />;
  }

  let initialValue;
  if (value) {
    initialValue = String(value);
  } else if (defaultValue) {
    initialValue = String(defaultValue);
  } else {
    initialValue = initialiseAsNull ? null : selectedValue;
  }

  let selectedOption: IndexableSelectOption;
  if (initialValue) {
    selectedOption = options.find((option: IndexableSelectOption) => {
      return option.const === initialValue;
    });
  }

  let initialSelectedText;
  if (selectedOption) {
    initialSelectedText = selectedOption.title || selectedOption.const;
  } else {
    initialSelectedText = !initialValue ? nullText : '';
  }

  const combineNullWithOptions = useCallback(
    (props: SelectProps): SelectProps => {
      const initialOption: SelectOption = {
        const: null,
        title: initialSelectedText,
      };
      const optionsWithNull = initialiseAsNull ? [initialOption, ...options] : options;

      return { ...props, options: optionsWithNull };
    },
    [props]
  );

  const composedOptionsProps = combineNullWithOptions(props);

  const reverseOptions = useCallback(
    (composedOptionsProps) => {
      const { options, ...otherProps } = composedOptionsProps;
      const newOptions = options.slice(0).reverse();
      const newObject = { options: newOptions, ...otherProps };

      return newObject;
    },
    [composedOptionsProps]
  );
  const reversedOptionsProps = reverseOptions(composedOptionsProps);

  const selectbox = useRef(null);
  const graphicalSelect = useRef(null);
  const optionsContainer = useRef(null);

  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [optionIndex, setOptionIndex] = useState<number>(-1);
  const [selectValue, setSelectValue] = useState<string>(initialValue);
  const [selectText, setSelectText] = useState<string>(initialSelectedText);
  const [openAbove, setOpenAbove] = useState(false);

  const openOptions = () => setOptionsOpen(true);
  const closeOptions = () => setOptionsOpen(false);

  const detectKey = (event: KeyboardEvent) => {
    const isSpace = event.key === ' ' || event.key === 'Spacebar';
    const isEnter = event.key === 'Enter';
    const isTab = event.key === 'Tab';
    const isEscape = event.key === 'Escape';
    const up = event.key === 'ArrowUp';
    const down = event.key === 'ArrowDown';
    const right = event.key === 'ArrowRight';
    const left = event.key === 'ArrowLeft';

    return { isSpace, isEnter, isTab, isEscape, up, down, right, left };
  };

  const handleSelect = (value: string, text: string): void => {
    closeOptions();
    setSelectValue(value);
    setSelectText(text);
    onSelectValue?.(value);
    selectbox.current.focus();
  };

  const handleFocus = (event: Event): void => {
    const optionsList = optionsContainer.current.children;
    const selectedOptionIndex = [...optionsContainer.current.children].indexOf(event.target);
    const current = optionsList[selectedOptionIndex];
    setSelectValue(current.dataset.value);
    setSelectText(current.dataset.text);
    setOptionIndex(selectedOptionIndex);
  };

  const handleDirectionsKeysOptionsClosed = ({ up, down, right, left }, optionsContainer): void => {
    const optionsList = optionsContainer.current?.children;
    if (!optionsList) return;
    const previous = up || left;
    const next = down || right;

    if (previous) {
      const current = optionsList[optionIndex - 1];
      if (optionIndex - 1 < 0) return;
      setSelectValue(current.dataset.value);
      setSelectText(current.dataset.text);
      setOptionIndex(optionIndex - 1);
    }

    if (next) {
      const current = optionsList[optionIndex + 1];
      const currentIsNullText = current.dataset.text === nullText;
      const amountToAdvance = currentIsNullText ? 2 : 1;
      const selectedOption = optionsList[optionIndex + amountToAdvance];

      if (optionIndex + 1 >= composedOptionsProps.options.length) return;
      setSelectValue(selectedOption.dataset.value);
      setSelectText(selectedOption.dataset.text);
      setOptionIndex(optionIndex + amountToAdvance);
    }
  };

  const handleDirectionKeys = ({ up, down, right, left }, optionsContainer) => {
    const optionsList = optionsContainer.current?.children;
    if (!optionsList) return;

    const previous = up || left;
    const next = down || right;

    if (previous) {
      const previousElem = optionsList[optionIndex - 1];
      if (optionIndex - 1 < 0) return;
      previousElem.focus();
    }

    if (next) {
      const nextElem = optionsList[optionIndex + 1];
      if (optionIndex + 1 >= composedOptionsProps.options.length) return;
      nextElem.focus();
    }
  };

  const handleOptionSelection = ({ isSpace, isEnter, isTab, isEscape }, value, text, optionsContainer) => {
    const spaceOrEnter = isSpace || isEnter;
    if (isEscape) {
      closeOptions();
      selectbox.current.focus();
    } else if (value && (isTab || spaceOrEnter)) {
      handleSelect(value, text);
    } else if (spaceOrEnter) {
      setOptionsOpen(!optionsOpen);
      if (optionsContainer?.current?.children) {
        const selectedOption = [...optionsContainer.current.children].filter((el) => el.innerText === selectValue);
        setTimeout(() => {
          if (selectedOption && selectedOption[0]) {
            selectedOption[0].focus();
          }
        }, 0);
      }
    }
  };

  const handleKeyDown = (event, value, text = null) => {
    const { isSpace, isEnter, isTab, isEscape, up, down, right, left } = detectKey(event);
    handleOptionSelection({ isSpace, isEnter, isTab, isEscape }, value, text, optionsContainer);
    const isDirectionKey = up || down || right || left;
    if (optionsOpen && isDirectionKey) {
      event.preventDefault();
      handleDirectionKeys({ up, down, right, left }, optionsContainer);
    }
    if (!optionsOpen && isDirectionKey) {
      event.preventDefault();
      handleDirectionsKeysOptionsClosed({ up, down, right, left }, optionsContainer);
    }
  };

  const isControlled = controlled;
  const handleOpenAbove = (boolean) => setOpenAbove(boolean);

  const optionsProps = openAbove ? { ...reversedOptionsProps } : { ...composedOptionsProps };

  return (
    <GraphicalSelect
      {...props}
      handleKeyDown={handleKeyDown}
      handleFocus={handleFocus}
      handleSelect={handleSelect}
      closeOptions={closeOptions}
      openOptions={openOptions}
      selectValue={selectValue}
      selectText={selectText}
      optionsOpen={optionsOpen}
      selectbox={selectbox}
      graphicalSelect={graphicalSelect}
      optionsContainer={optionsContainer}
      controlledText={controlledText}
      isControlled={isControlled}
      className={className}
      placeholder={placeholder}
      renderOptionsList={(selectProps) => <OptionsList {...optionsProps} {...selectProps} id={selectId.current} />}
      optionsLength={composedOptionsProps.options.length}
      setOpenAbove={handleOpenAbove}
      openAbove={openAbove}
      color={color}
      invalid={invalid}
      errorMsgHolderId={errorMsgHolderId}
      id={selectId.current}
    />
  );
};

export default Select;
